import {type IAttributeTemplate} from '../schema';
import {
	emptyValueSymbol,
	ATTRIBUTE_NUMBER_SPEC_2_TYPE,
	ATTRIBUTE_NUMBER_MAX_DECIMAL,
	ATTRIBUTE_NUMBER_TESTS,
} from '../constants';

/**
 * @return Decimals
 */
export const getDecimalCount = (value: number | string) => {
	value = value.toString();
	const decimalSeparator = whatDecimalSeparator();
	const decimals = value.split(decimalSeparator);
	return decimals[1]?.length || 0;
};

/**
 * https://stackoverflow.com/questions/11665884/how-can-i-parse-a-string-with-a-comma-thousand-separator-to-a-number
 */
export const parseNumber = (value: string | number, locale?: string) => {
	value = value.toString();
	locale = locale || navigator.language;
	const example = Intl.NumberFormat(locale).format(1.1);
	const cleanPattern = new RegExp('[^-+0-9' + example.charAt(1) + ']', 'g');
	return parseFloat(
		value.replace(cleanPattern, '').replace(example.charAt(1), '.'),
	);
};

/**
 * @return Decimal separator for locale
 */
export const whatDecimalSeparator = () =>
	(1.1).toLocaleString().substring(1, 2);

export const number = (
	value: any,
	{spec1, spec2}: Pick<IAttributeTemplate, 'spec1' | 'spec2'>,
) => {
	value = Array.isArray(value) ? value[0] : value;
	if (value === '' || value == null || value === emptyValueSymbol) return true;

	const numberType = spec2;
	// patch for folks that may have had the unlimited type with a spec1 of 11 (meant as the default for whole numbers)
	const maxLength =
		numberType !== ATTRIBUTE_NUMBER_SPEC_2_TYPE.UNLIMITED
			? spec1 || Infinity
			: Infinity;

	// const test = STR_TESTS[numberType];
	const maxDecimal =
		ATTRIBUTE_NUMBER_MAX_DECIMAL[
			numberType as keyof typeof ATTRIBUTE_NUMBER_MAX_DECIMAL
		];

	value = value.toString();

	const decimalIndex = value.indexOf('.');
	if (decimalIndex > -1 && decimalIndex + 1 === value.length)
		return 'Invalid number';

	if (value.includes('-') && value.charAt(0) !== '-') {
		const newValue = value.replace(/(\d)-|(-)-/g, '$1$2');
		if (newValue !== value) return 'Invalid number';
		value = newValue;
	}

	if (value.length > maxLength) return `Exceeds max length of ${maxLength}`;

	if (isNaN(parseNumber(value))) return 'Invalid number format';

	// Negative numbers are not allowed by the DAM
	if (numberType === ATTRIBUTE_NUMBER_SPEC_2_TYPE.UNLIMITED && value < 0)
		return 'Negative numbers are not allowed';

	const decimalCount = getDecimalCount(value);
	if (numberType === ATTRIBUTE_NUMBER_SPEC_2_TYPE.WHOLE && !!decimalCount)
		return `Must be a whole number`;
	if (decimalCount > maxDecimal) return `Exceeds max decimals of ${maxDecimal}`;

	return true;
};

export default number;
